import { useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Skeleton } from '@/common/ui/skeleton'
import { Badge } from '@/common/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Server, Clock, Activity } from 'lucide-react'
import MetricsChart from '../MetricsChart'
import useQueryGetTaskMetrics from '../../api/hooks/useQueryGetTaskMetrics'
import { TaskMetric } from '../../type'

interface TaskDetailModalProps {
	task: TaskMetric | null
	open: boolean
	onOpenChange: (open: boolean) => void
}

export default function TaskDetailModal({
	task,
	open,
	onOpenChange,
}: TaskDetailModalProps) {
	const [minutes, setMinutes] = useState(60)
	const [periodSeconds, setPeriodSeconds] = useState(60)

	const { data, isLoading } = useQueryGetTaskMetrics(
		{
			taskId: task?.taskId || '',
			minutes,
			periodSeconds,
		},
		{
			enabled: open && !!task?.taskId,
		},
	)

	if (!task) return null

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
				<DialogHeader className="border-b pb-4">
					<div className="flex items-center gap-3">
						<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
							<Server className="h-6 w-6 text-white" />
						</div>
						<div>
							<DialogTitle className="text-2xl font-bold">
								Task Metrics: {task.taskName}
							</DialogTitle>
							<DialogDescription className="mt-1 text-base">
								CPU and Memory utilization over time for this task
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="mt-6 space-y-6">
					{/* Controls */}
					<Card className="border-2 bg-gradient-to-br from-muted/30 to-background">
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-lg">
								<Clock className="h-5 w-5" />
								การตั้งค่า
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex flex-wrap items-center gap-4">
								<div className="flex items-center gap-2 rounded-lg border bg-card p-2">
									<label className="whitespace-nowrap text-sm font-medium">
										ช่วงเวลา:
									</label>
									<Select
										value={minutes.toString()}
										onValueChange={(value) => setMinutes(Number(value))}
									>
										<SelectTrigger className="w-[140px] border-0 shadow-none">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="15">15 นาที</SelectItem>
											<SelectItem value="30">30 นาที</SelectItem>
											<SelectItem value="60">1 ชั่วโมง</SelectItem>
											<SelectItem value="120">2 ชั่วโมง</SelectItem>
											<SelectItem value="240">4 ชั่วโมง</SelectItem>
											<SelectItem value="480">8 ชั่วโมง</SelectItem>
											<SelectItem value="1440">24 ชั่วโมง</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="flex items-center gap-2 rounded-lg border bg-card p-2">
									<label className="whitespace-nowrap text-sm font-medium">
										ความละเอียด:
									</label>
									<Select
										value={periodSeconds.toString()}
										onValueChange={(value) => setPeriodSeconds(Number(value))}
									>
										<SelectTrigger className="w-[140px] border-0 shadow-none">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="60">1 นาที</SelectItem>
											<SelectItem value="300">5 นาที</SelectItem>
											<SelectItem value="600">10 นาที</SelectItem>
											<SelectItem value="1800">30 นาที</SelectItem>
											<SelectItem value="3600">1 ชั่วโมง</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Task Info */}
					<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
						<Card className="border-2 transition-all hover:shadow-lg">
							<CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100/50 pb-3 dark:from-blue-950/50 dark:to-blue-900/30">
								<CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
									<Activity className="h-4 w-4" />
									Status
								</CardTitle>
							</CardHeader>
							<CardContent className="pt-4">
								<Badge
									variant={
										task.lastStatus === 'RUNNING'
											? 'default'
											: task.lastStatus === 'PENDING'
												? 'secondary'
												: 'outline'
									}
									className="px-3 py-1.5 text-base font-semibold"
								>
									{task.lastStatus}
								</Badge>
							</CardContent>
						</Card>
					</div>

					{/* Charts */}
					<div className="space-y-6">
						{isLoading ? (
							<div className="space-y-4">
								<Skeleton className="h-80 w-full rounded-lg" />
								<Skeleton className="h-80 w-full rounded-lg" />
							</div>
						) : (
							<>
								<MetricsChart
									data={data?.cpu ?? []}
									title={`CPU Utilization - ${task.taskName}`}
									description="แสดงการใช้งาน CPU ตามช่วงเวลา"
									color="#3b82f6"
									isLoading={isLoading}
								/>
								<MetricsChart
									data={data?.memory ?? []}
									title={`Memory Utilization - ${task.taskName}`}
									description="แสดงการใช้งาน Memory ตามช่วงเวลา"
									color="#8b5cf6"
									isLoading={isLoading}
								/>
							</>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
